import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { chatbotApi } from '../services/chatbotApi';
import { useAuth } from '../hooks/useAuth';
import { generateSessionId } from '../utils/storage';
import { shouldSuppressAuthErrorToast } from '../services/authErrorSuppression';
import toast from 'react-hot-toast';

export interface ChatbotMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  metadata?: {
    guidelines_applied?: number;
    medical_safety_level?: 'standard' | 'elevated' | 'critical';
    requires_escalation?: boolean;
    requires_human_review?: boolean;
    tools_executed?: string[];
    processing_time_ms?: number;
    system_message?: boolean;
    emergency_shutdown?: boolean;
    blocked_access?: boolean;
  };
}

export interface ChatbotSession {
  id: string;
  session_id: string; // Backend session ID
  title: string;
  messages: ChatbotMessage[];
  created_at: Date;
  updated_at: Date;
  user_type: 'patient' | 'staff' | 'visitor';
  context?: Record<string, any>;
  is_escalated?: boolean;
  is_archived?: boolean;
  favorite?: boolean;
  tags?: string[];
  category?: string;
  summary?: string;
  message_count?: number;
  last_message_at?: Date;
}

interface ChatbotSessionContextType {
  // Current session
  currentSession: ChatbotSession | null;
  setCurrentSession: (session: ChatbotSession | null) => void;
  
  // All sessions
  sessions: ChatbotSession[];
  setSessions: (sessions: ChatbotSession[]) => void;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Session management
  createNewSession: (userType?: 'patient' | 'staff' | 'visitor', context?: Record<string, any>) => Promise<ChatbotSession>;
  updateSession: (sessionId: string, updates: Partial<ChatbotSession>) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  unarchiveSession: (sessionId: string) => Promise<void>;
  toggleFavorite: (sessionId: string) => Promise<void>;
  
  // Message management
  addMessage: (sessionId: string, message: Omit<ChatbotMessage, 'id'>) => Promise<void>;
  updateMessage: (sessionId: string, messageId: string, updates: Partial<ChatbotMessage>) => void;
  
  // Session loading and synchronization
  loadSessions: () => Promise<void>;
  loadSessionDetail: (sessionId: string) => Promise<ChatbotSession | null>;
  refreshSession: (sessionId: string) => Promise<void>;
  
  // Session selection
  selectSession: (sessionId: string) => Promise<void>;
  
  // Utility
  generateSessionTitle: (messages: ChatbotMessage[]) => string;
  
  // Migration helpers
  migrateLocalStorageSessions: () => Promise<void>;
}

const ChatbotSessionContext = createContext<ChatbotSessionContextType | undefined>(undefined);

export const useChatbotSession = (): ChatbotSessionContextType => {
  const context = useContext(ChatbotSessionContext);
  if (!context) {
    throw new Error('useChatbotSession must be used within a ChatbotSessionProvider');
  }
  return context;
};

interface ChatbotSessionProviderProps {
  children: ReactNode;
}

export const ChatbotSessionProvider: React.FC<ChatbotSessionProviderProps> = ({ children }) => {
  const { user, isAuthenticated, validationState } = useAuth();
  const [currentSession, setCurrentSession] = useState<ChatbotSession | null>(null);
  const [sessions, setSessions] = useState<ChatbotSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load sessions from backend when user is authenticated and validated
  useEffect(() => {
    if (isAuthenticated && user && validationState === 'valid') {
      loadSessions();
    } else {
      // Clear sessions when user logs out or validation not complete
      setSessions([]);
      setCurrentSession(null);
    }
  }, [isAuthenticated, user, validationState]);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated || validationState !== 'valid') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await chatbotApi.getChatHistory({
        limit: 50,
        archived: false
      });
      
      // Convert backend response to frontend session format, filtering out sessions with no messages
      const convertedSessions: ChatbotSession[] = response.results
        .filter((item: any) => (item.message_count || 0) > 0) // Only include sessions with messages
        .map((item: any) => ({
          id: item.session_id, // Use session_id as ID for consistency
          session_id: item.session_id,
          title: item.title || 'AI Conversation',
          messages: [], // Messages will be loaded on demand
          created_at: new Date(item.created_at),
          updated_at: new Date(item.updated_at),
          last_message_at: new Date(item.last_message_at),
          user_type: 'patient', // Default, will be updated when loaded
          is_archived: item.archived || false,
          favorite: item.favorite || false,
          tags: item.tags || [],
          category: item.category || 'general',
          summary: item.summary || '',
          message_count: item.message_count || 0
        }));
      
      setSessions(convertedSessions);
    } catch (err: any) {
      // Suppress 401 errors during initial load (expected when not authenticated)
      if (err?.response?.status === 401 && validationState !== 'valid') {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('Expected 401 during initial load, skipping chatbot session load');
        }
        setSessions([]);
        setLoading(false);
        return;
      }
      
      console.error('Error loading chatbot sessions:', err);
      setError(err.message || 'Failed to load chat sessions');
      // Suppress the toast if a logout is in flight / the user is no longer
      // authenticated — they're being redirected to login already.
      if (!shouldSuppressAuthErrorToast()) {
        toast.error('Failed to load chat history');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, validationState]);

  const loadSessionDetail = useCallback(async (sessionId: string): Promise<ChatbotSession | null> => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await chatbotApi.getChatHistoryDetail(sessionId);
      
      // Convert backend messages to frontend format
      const convertedMessages: ChatbotMessage[] = response.messages.map((msg: any) => ({
        id: msg.id,
        content: msg.content,
        sender: msg.role === 'user' ? 'user' : 'assistant',
        timestamp: new Date(msg.created_at),
        metadata: msg.metadata || {}
      }));
      
      const detailedSession: ChatbotSession = {
        id: response.session_id,
        session_id: response.session_id,
        title: response.title || 'AI Conversation',
        messages: convertedMessages,
        created_at: new Date(response.created_at),
        updated_at: new Date(response.updated_at),
        last_message_at: new Date(response.last_message_at),
        user_type: 'patient', // Default
        is_archived: response.archived || false,
        favorite: response.favorite || false,
        tags: response.tags || [],
        category: response.category || 'general',
        summary: response.summary || '',
        message_count: response.total_messages || 0
      };
      
      return detailedSession;
    } catch (err: any) {
      console.error('Error loading session detail:', err);
      toast.error('Failed to load conversation details');
      return null;
    }
  }, [isAuthenticated]);

  const createNewSession = useCallback(async (
    userType: 'patient' | 'staff' | 'visitor' = 'patient',
    context?: Record<string, any>
  ): Promise<ChatbotSession> => {
    if (!isAuthenticated || !user) {
      throw new Error('Authentication required to create session');
    }

    const sessionId = generateSessionId();
    
    // Create new session that will be persisted on first message
    const newSession: ChatbotSession = {
      id: sessionId,
      session_id: sessionId,
      title: generateSessionTitle([]),
      messages: [],
      created_at: new Date(),
      updated_at: new Date(),
      last_message_at: new Date(),
      user_type: userType,
      context,
      is_escalated: false,
      is_archived: false,
      favorite: false,
      tags: [],
      category: 'general',
      summary: '',
      message_count: 0
    };

    // Don't add to sessions list until it has messages
    // setSessions(prev => [newSession, ...prev]);
    setCurrentSession(newSession);
    
    return newSession;
  }, [isAuthenticated, user]);

  const updateSession = useCallback(async (sessionId: string, updates: Partial<ChatbotSession>) => {
    try {
      // Update backend if session exists there
      if (sessions.find(s => s.session_id === sessionId)) {
        await chatbotApi.updateChatHistory(sessionId, {
          title: updates.title,
          summary: updates.summary,
          category: updates.category,
          archived: updates.is_archived,
          favorite: updates.favorite,
          tags: updates.tags
        });
      }
      
      // Update local state
      setSessions(prev => prev.map(session => {
        if (session.session_id === sessionId) {
          const updatedSession = {
            ...session,
            ...updates,
            updated_at: new Date()
          };
          
          // Update current session if it's the one being updated
          if (currentSession?.session_id === sessionId) {
            setCurrentSession(updatedSession);
          }
          
          return updatedSession;
        }
        return session;
      }));
    } catch (err: any) {
      console.error('Error updating session:', err);
      toast.error('Failed to update conversation');
    }
  }, [sessions, currentSession]);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await chatbotApi.deleteChatHistory(sessionId, {
        reason: 'user_request',
        notes: 'Deleted by user from AI chat interface'
      });
      
      setSessions(prev => prev.filter(session => session.session_id !== sessionId));
      
      // Clear current session if it was deleted
      if (currentSession?.session_id === sessionId) {
        setCurrentSession(null);
      }
      
      toast.success('Conversation deleted successfully');
    } catch (err: any) {
      console.error('Error deleting session:', err);
      toast.error('Failed to delete conversation');
    }
  }, [currentSession]);

  const archiveSession = useCallback(async (sessionId: string) => {
    try {
      await chatbotApi.archiveChatHistory(sessionId);
      await updateSession(sessionId, { is_archived: true });
      toast.success('Conversation archived');
    } catch (err: any) {
      console.error('Error archiving session:', err);
      toast.error('Failed to archive conversation');
    }
  }, [updateSession]);

  const unarchiveSession = useCallback(async (sessionId: string) => {
    try {
      await chatbotApi.unarchiveChatHistory(sessionId);
      await updateSession(sessionId, { is_archived: false });
      toast.success('Conversation unarchived');
    } catch (err: any) {
      console.error('Error unarchiving session:', err);
      toast.error('Failed to unarchive conversation');
    }
  }, [updateSession]);

  const toggleFavorite = useCallback(async (sessionId: string) => {
    try {
      const response = await chatbotApi.toggleChatHistoryFavorite(sessionId);
      await updateSession(sessionId, { favorite: response.favorite });
      toast.success(response.favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      toast.error('Failed to update favorite status');
    }
  }, [updateSession]);

  const addMessage = useCallback(async (sessionId: string, message: Omit<ChatbotMessage, 'id'>) => {
    const newMessage: ChatbotMessage = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: message.timestamp || new Date()
    };

    // Update local state immediately for optimistic UI
    setSessions(prev => {
      const existingSessionIndex = prev.findIndex(s => s.session_id === sessionId);
      
      if (existingSessionIndex >= 0) {
        // Update existing session
        return prev.map(session => {
          if (session.session_id === sessionId) {
            const updatedMessages = [...session.messages, newMessage];
            const updatedSession = {
              ...session,
              messages: updatedMessages,
              title: session.messages.length === 0 ? generateSessionTitle(updatedMessages) : session.title,
              updated_at: new Date(),
              last_message_at: new Date(),
              message_count: updatedMessages.length
            };

            // Update current session if it's the one being updated
            if (currentSession?.session_id === sessionId) {
              setCurrentSession(updatedSession);
            }

            return updatedSession;
          }
          return session;
        });
      } else {
        // Add new session to the list (this happens on first message)
        if (currentSession?.session_id === sessionId) {
          const updatedMessages = [...currentSession.messages, newMessage];
          const updatedSession = {
            ...currentSession,
            messages: updatedMessages,
            title: generateSessionTitle(updatedMessages),
            updated_at: new Date(),
            last_message_at: new Date(),
            message_count: updatedMessages.length
          };
          
          setCurrentSession(updatedSession);
          return [updatedSession, ...prev];
        }
        return prev;
      }
    });

    // Note: The backend persistence happens automatically when messages are sent through chatbotApi.sendMessage
    // This method is primarily for optimistic UI updates
  }, [currentSession]);

  const updateMessage = useCallback((sessionId: string, messageId: string, updates: Partial<ChatbotMessage>) => {
    setSessions(prev => prev.map(session => {
      if (session.session_id === sessionId) {
        const updatedMessages = session.messages.map(msg =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        );
        const updatedSession = {
          ...session,
          messages: updatedMessages,
          updated_at: new Date()
        };

        // Update current session if it's the one being updated
        if (currentSession?.session_id === sessionId) {
          setCurrentSession(updatedSession);
        }

        return updatedSession;
      }
      return session;
    }));
  }, [currentSession]);

  const selectSession = useCallback(async (sessionId: string) => {
    // First check if session is already in memory with messages
    const existingSession = sessions.find(s => s.session_id === sessionId);
    if (existingSession && existingSession.messages.length > 0) {
      setCurrentSession(existingSession);
      return;
    }

    // Load session detail from backend
    const detailedSession = await loadSessionDetail(sessionId);
    if (detailedSession) {
      setCurrentSession(detailedSession);
      
      // Also update the session in the sessions list
      setSessions(prev => prev.map(session => 
        session.session_id === sessionId ? detailedSession : session
      ));
    }
  }, [sessions, loadSessionDetail]);

  const refreshSession = useCallback(async (sessionId: string) => {
    const detailedSession = await loadSessionDetail(sessionId);
    if (detailedSession) {
      // Update both current session and sessions list
      setSessions(prev => prev.map(session => 
        session.session_id === sessionId ? detailedSession : session
      ));
      
      if (currentSession?.session_id === sessionId) {
        setCurrentSession(detailedSession);
      }
    }
  }, [currentSession, loadSessionDetail]);

  const generateSessionTitle = useCallback((messages: ChatbotMessage[]): string => {
    if (messages.length === 0) return 'AI Assistant Chat';
    
    const firstUserMessage = messages.find(msg => msg.sender === 'user');
    if (firstUserMessage) {
      // Take first 50 characters and add ellipsis if longer
      const title = firstUserMessage.content.trim();
      return title.length > 50 ? title.substring(0, 47) + '...' : title;
    }
    
    return 'AI Assistant Conversation';
  }, []);

  const migrateLocalStorageSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const savedSessions = localStorage.getItem('ai_chat_sessions');
      if (savedSessions) {
        const localSessions = JSON.parse(savedSessions);
        
        // For each local session, we could potentially create a backend session
        // But for now, we'll just clear localStorage since the backend is the source of truth
        localStorage.removeItem('ai_chat_sessions');
        localStorage.removeItem('current_session_id');
        
        console.log('Cleared localStorage AI chat sessions - using backend persistence');
      }
    } catch (error) {
      console.error('Error migrating localStorage sessions:', error);
    }
  }, [isAuthenticated]);

  const value: ChatbotSessionContextType = {
    currentSession,
    setCurrentSession,
    sessions,
    setSessions,
    loading,
    error,
    createNewSession,
    updateSession,
    deleteSession,
    archiveSession,
    unarchiveSession,
    toggleFavorite,
    addMessage,
    updateMessage,
    loadSessions,
    loadSessionDetail,
    refreshSession,
    selectSession,
    generateSessionTitle,
    migrateLocalStorageSessions
  };

  return (
    <ChatbotSessionContext.Provider value={value}>
      {children}
    </ChatbotSessionContext.Provider>
  );
};